import type * as api from '@gitbook/api-client';
import { createPalette, PaletteCommandType } from '@gitbook/react-uikit';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { mapSuspenseArray } from '@gitbook/util-suspense-cache';

import { apiResource, readAPICache } from '../../state';
import { UserAvatar } from '../Users';
import { requestReviewersForChangeRequest } from './requestReviewersForChangeRequest';
import type { ChangeRequestPendingRequestedReviewer } from './useChangeRequestReviewers';

export function useAddReviewerPalette(props: {
    changeRequest: api.ChangeRequest;
    space: api.Space;
    suggestions: ChangeRequestPendingRequestedReviewer[];
    pendingRequestedReviewers: ChangeRequestPendingRequestedReviewer[];
    existingReviewers: Set<string>;
    onPendingRequestedReviewersChange?: (
        pendingRequestedReviewers: ChangeRequestPendingRequestedReviewer[]
    ) => void;
}) {
    const {
        changeRequest,
        space,
        suggestions,
        pendingRequestedReviewers,
        existingReviewers,
        onPendingRequestedReviewersChange = async (update) => {
            await requestReviewersForChangeRequest({
                spaceId: space.id,
                changeRequestId: changeRequest.id,
                users: update.map((reviewer) => reviewer.userId),
            });
        },
    } = props;

    return useEventCallback(() => {
        return createPalette({
            search: {
                placeholder: 'Type or choose a user…',
            },
            groups: [
                {
                    key: 'suggestions',
                    title: 'Suggestions',
                    commands: () => {
                        return mapSuspenseArray(suggestions, (suggestion) => {
                            const user = readAPICache(
                                apiResource('getUserById', {
                                    userId: suggestion.userId,
                                })
                            );

                            return {
                                key: suggestion.userId,
                                type: PaletteCommandType.CHECKBOX,
                                selected: false,
                                title: user.displayName,
                                icon: <UserAvatar user={user} size="sm" />,
                                trigger: () => {
                                    return onPendingRequestedReviewersChange([
                                        ...pendingRequestedReviewers,
                                        suggestion,
                                    ]);
                                },
                            };
                        });
                    },
                },
                {
                    key: 'users',
                    title: 'Everyone else',
                    commands: (query) => {
                        const response = readAPICache(
                            apiResource(
                                'listMembersInOrganizationById',
                                {
                                    organizationId: space.organization,
                                    search: query,
                                    limit: 8,
                                },
                                { nullOnError: true }
                            )
                        );
                        if (!response) {
                            return [];
                        }

                        return response.items.map((member) => {
                            const selected = existingReviewers.has(member.user.id);
                            return {
                                key: member.user.id,
                                type: PaletteCommandType.CHECKBOX,
                                selected,
                                disabled: selected,
                                icon: () => (
                                    <UserAvatar user={member.user} size="md" tooltip={false} />
                                ),
                                title: member.user.displayName,
                                subtitle: member.user.email,
                                trigger: () => {
                                    if (selected) {
                                        return;
                                    }

                                    return onPendingRequestedReviewersChange([
                                        ...pendingRequestedReviewers,
                                        { kind: 'user', userId: member.user.id },
                                    ]);
                                },
                            };
                        });
                    },
                },
            ],
        });
    });
}
